import Button from "@/components/ui/Button";
import Checkbox from "@/components/ui/CheckBox";
import TextField from "@/components/ui/Textfield";
import React, { useState } from "react";
import useOnboardingStore from "../store/onboardingStore";
import useGlobalStore from "@/store/globalStore";
import Dropdown from "@/components/ui/DropDown";

interface Props {
  onNext: () => void;
}

/**
 * Formats a raw digit string into (XXX) XXX-XXXX as the user types.
 */
const formatPhoneNumber = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const StepTwoMediaAgencyOnboarding: React.FC<Props> = ({ onNext }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    name: "",
    website: "",
    phone: "",
    country_code: "+1",
    industry: "",
    size: "",
    logo: null,
    previewLogo: null,
    shipping: {
      address_line_1: "",
      address_line_2: "",
      city: "",
      state: "",
      country: "",
      zip_postal: "",
    },
    billing: {
      address_line_1: "",
      address_line_2: "",
      city: "",
      state: "",
      country: "",
      zip_postal: "",
    },
    isSameAddress: true,
  });

  const createMediaAgency = useOnboardingStore(
    (state) => state.createMediaAgency,
  );

  const setError = useGlobalStore((state) => state.setError);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name.includes(".")) {
      const [section, key] = name.split(".");

      setFormData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section as keyof typeof prev],
          [key]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData((prev) => ({ ...prev, phone: formatted }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const {
      name,
      website,
      phone,
      country_code,
      industry,
      size,
      logo,
      shipping,
      billing,
      isSameAddress,
    } = formData;

    if (!name.trim()) return setError("Company name is required.");
    if (!phone.trim()) return setError("Phone number is required.");
    if (!industry.trim()) return setError("Industry is required.");
    if (!size.trim()) return setError("Number of employees is required.");

    // Validate phone has 10 digits
    const phoneDigits = phone.replace(/\D/g, "");
    if (phoneDigits.length < 10) {
      return setError("Phone number must be 10 digits.");
    }

    const requiredShippingFields = [
      shipping.address_line_1,
      shipping.city,
      shipping.state,
      shipping.country,
      shipping.zip_postal,
    ];
    if (requiredShippingFields.some((field) => !field.trim())) {
      return setError("Complete address is required.");
    }

    if (!isSameAddress) {
      const requiredBillingFields = [
        billing.address_line_1,
        billing.city,
        billing.state,
        billing.country,
        billing.zip_postal,
      ];
      if (requiredBillingFields.some((field) => !field.trim())) {
        return setError("Complete billing address is required.");
      }
    }

    const finalBilling = isSameAddress ? { ...shipping } : billing;

    try {
      setIsLoading(true);
      await createMediaAgency({
        name,
        website,
        phone: `${country_code} ${phone}`.trim(),
        industry,
        size,
        logo,
        shipping,
        billing: finalBilling,
      });

      onNext();
    } catch (err: any) {
      setError(err?.message || "Failed to submit user data.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && (file.type === "image/png" || file.type === "image/jpeg")) {
      setFormData({
        ...formData,
        logo: file,
        previewLogo: URL.createObjectURL(file),
      });
    } else {
      setError("Only PNG or JPEG files are allowed.");
    }
  };

  return (
    <div className="bg-[#F8F8F8] min-h-screen flex flex-col items-center justify-center px-4 py-14">
      <div className="max-w-lg w-full text-center mb-6">
        <h1 className="text-5xl font-bold text-[#EA33C0] mb-4">MediaBridge</h1>
        <h2 className="text-xl font-semibold">
          Great to meet you!
          <br />
          Just a few more details about your company.
        </h2>
        <p className="text-sm text-gray-800 mt-4">
          Your company information will be used to build your team account.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="w-full max-w-xl space-y-5 mt-2">
        <div className="flex items-center">
          <div className="mr-5 w-56 text-right font-semibold">Company Name</div>
          <TextField
            type="text"
            name="name"
            value={formData.name}
            onChange={onChange}
            placeholder="Company Name"
            className="w-full"
          />
        </div>

        <div className="flex items-center">
          <div className="mr-5 w-56 text-right font-semibold">Website</div>
          <TextField
            type="text"
            name="website"
            value={formData.website}
            onChange={onChange}
            placeholder="Website"
            className="w-full"
          />
        </div>

        <div className="flex items-center">
          <div className="mr-5 w-56 text-right font-semibold">Phone</div>
          <div className="flex gap-2 items-center w-full">
            <div className="w-32">
              <Dropdown
                selected={formData.country_code}
                setSelected={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    country_code: e.toString(),
                  }));
                }}
                options={[
                  { title: "+1 (US/CA)", value: "+1" },
                  { title: "+44 (UK)", value: "+44" },
                  { title: "+91 (IN)", value: "+91" },
                  { title: "+61 (AU)", value: "+61" },
                  { title: "+49 (DE)", value: "+49" },
                  { title: "+33 (FR)", value: "+33" },
                ]}
              />
            </div>
            <TextField
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handlePhoneChange}
              placeholder="(123) 456-7890"
              className="w-full"
            />
          </div>
        </div>

        <div className="flex items-center">
          <div className="mr-5 w-56 text-right font-semibold">Industry</div>
          <Dropdown
            options={[
              { title: "Media", value: "media" },
              { title: "Other", value: "Other" },
            ]}
            selected={formData.industry}
            setSelected={(option) => {
              setFormData((prev) => ({
                ...prev,
                industry: option.toString(),
              }));
            }}
            className="w-full"
          />
        </div>

        <div className="flex items-center">
          <div className="mr-5 w-56 text-right font-semibold">Employees</div>
          <Dropdown
            options={[
              { title: "1-10", value: "1-10" },
              { title: "10-50", value: "10-50" },
              { title: "50+", value: "50+" },
            ]}
            selected={formData.size}
            setSelected={(option) => {
              setFormData((prev) => ({
                ...prev,
                size: option.toString(),
              }));
            }}
            className="w-full"
          />
        </div>

        <div className="flex items-start">
          <div className="mr-5 w-56 text-right font-semibold">Company Logo</div>
          <label className="w-full h-[200px] border border-[#EA33C0] rounded-lg flex flex-col items-center justify-center cursor-pointer bg-gray-50 hover:bg-gray-200 transition relative overflow-hidden">
            {formData.previewLogo ? (
              <img
                src={formData.previewLogo}
                alt="Preview"
                className="object-contain max-w-full max-h-full"
              />
            ) : (
              <>
                <div className="text-3xl text-gray-400">+</div>
                <p className="text-gray-600 text-sm text-center mt-2">
                  Add Image
                  <br />
                  (PNG, JPG or JPEG)
                </p>
              </>
            )}
            <input
              type="file"
              accept="image/png, image/jpeg, image/jpg"
              onChange={handleImageChange}
              className="hidden"
            />
          </label>
        </div>

        <div className="space-y-4">
          <div className="flex items-center">
            <div className="mr-5 w-56 text-right font-semibold">Address</div>
            <TextField
              type="text"
              name="shipping.address_line_1"
              value={formData.shipping.address_line_1}
              onChange={onChange}
              placeholder="Address"
              className="w-full"
            />
          </div>

          <div className="flex items-center">
            <div className="mr-5 w-56"></div>
            <TextField
              type="text"
              name="shipping.address_line_2"
              value={formData.shipping.address_line_2}
              onChange={onChange}
              placeholder="Address 2"
              className="w-full"
            />
          </div>

          <div className="flex items-center">
            <div className="mr-4 w-56"></div>
            <div className="flex w-full">
              <TextField
                type="text"
                name="shipping.city"
                value={formData.shipping.city}
                onChange={onChange}
                placeholder="City"
                className="w-full mr-3"
              />
              <TextField
                type="text"
                name="shipping.state"
                value={formData.shipping.state}
                onChange={onChange}
                placeholder="State/Province"
                className="w-full"
              />
            </div>
          </div>

          <div className="flex items-center">
            <div className="mr-4 w-56"></div>
            <div className="flex w-full">
              <TextField
                type="text"
                name="shipping.country"
                value={formData.shipping.country}
                onChange={onChange}
                placeholder="Country"
                className="w-full mr-3"
              />
              <TextField
                type="text"
                name="shipping.zip_postal"
                value={formData.shipping.zip_postal}
                onChange={onChange}
                placeholder="Postal Code"
                className="w-full"
              />
            </div>
          </div>
        </div>

        <div className="flex">
          <div className="mr-5 w-40 text-right font-semibold">
            Billing Address
          </div>
          <Checkbox
            checked={formData.isSameAddress}
            onChange={(newValue) => {
              setFormData({
                ...formData,
                isSameAddress: newValue,
              });
            }}
          />
          <span className="ml-2">Same as shipping address</span>
        </div>

        {!formData.isSameAddress && (
          <div className="space-y-4">
            <div className="flex items-center">
              <div className="mr-5 w-56 text-right"></div>
              <TextField
                type="text"
                name="billing.address_line_1"
                value={formData.billing.address_line_1}
                onChange={onChange}
                placeholder="Address"
                className="w-full"
              />
            </div>

            <div className="flex items-center">
              <div className="mr-5 w-56"></div>
              <TextField
                type="text"
                name="billing.address_line_2"
                value={formData.billing.address_line_2}
                onChange={onChange}
                placeholder="Address 2"
                className="w-full"
              />
            </div>

            <div className="flex items-center">
              <div className="mr-7 w-40"></div>
              <div className="flex w-full">
                <TextField
                  type="text"
                  name="billing.city"
                  value={formData.billing.city}
                  onChange={onChange}
                  placeholder="City"
                  className="w-full mr-3"
                />
                <TextField
                  type="text"
                  name="billing.state"
                  value={formData.billing.state}
                  onChange={onChange}
                  placeholder="State/Province"
                  className="w-full"
                />
              </div>
            </div>

            <div className="flex items-center">
              <div className="mr-7 w-40"></div>
              <div className="flex w-full">
                <TextField
                  type="text"
                  name="billing.country"
                  value={formData.billing.country}
                  onChange={onChange}
                  placeholder="Country"
                  className="w-full mr-3"
                />
                <TextField
                  type="text"
                  name="billing.zip_postal"
                  value={formData.billing.zip_postal}
                  onChange={onChange}
                  placeholder="Postal Code"
                  className="w-full"
                />
              </div>
            </div>
          </div>
        )}

        <div className="pt-4 text-center flex justify-center">
          <Button type="primary" onClick={handleSubmit} isLoading={isLoading}>
            Save Info
          </Button>
        </div>
      </form>
    </div>
  );
};

export default StepTwoMediaAgencyOnboarding;
